
import { useEffect, useMemo, useRef, useState } from 'react';
import CustomToolTip from '../../../../../../CommonComponents/CustomToolTip'
import { Command, CustomScheduleModalFormType, RecentlyEditedTelecommandType, ScheduleData, ScheduleStatusType, ScheduleTask } from '../../../../../types';
import { TaskNotPerformedSvg, TaskUpcommingSvg } from '../../../modals/Svgs'
import { ChevronDownSvg, AddNewCommandIcon, TeleCommandsIcon, NoCommandSelectedIcon, NoSchedulePreview } from '../../Svgs'
import EmptyPage from '../../../../../custom_components/EmptyPage';
import NewAddButton from './NewAddButton';
import GroupButtonList from '../../../../../../CommonComponents/GroupButtonList';
import { get_task_status_icon, getContactCurrentStatus, getTaskTypeDisplayLabel, MenuList } from '../../../../../utils';
import { base64ToHex } from '../../../../../../../utils/commonFunctions';
import { scrollAndBlinkEditedRow } from './scheduleRowUtils';

type TabType = 'telecommand' | 'raw-data'

const TabOptions = [
    { label: 'Telecommand', value: 'telecommand' },
    { label: 'Raw Data', value: 'raw-data' },
]
interface ScheduleTaskViewProps {
    showAddButton?: CustomScheduleModalFormType;
    readonly: boolean;
    currentTime?: number;
    selectedSchedule?: ScheduleData | null;
    allTasks: ScheduleTask[];
    selectedTask: ScheduleTask | null;
    selectedTelecommand: Command | null;
    recentlyEditedTelecommand?: RecentlyEditedTelecommandType | null;
    onMenuClick: (menu: string, view: 'readonly' | 'editable', type: string, data: { name: string, scheduleId: string, taskId?: string, commandId?: string }) => void;
    onTaskSelect: (view: 'readonly' | 'editable', task: ScheduleTask) => void;
    onCommandSelect: (view: 'readonly' | 'editable', command: Command) => void;
    onAddTask?: (task: ScheduleTask) => void;
    onAddCommand?: (task: ScheduleTask, command: Command | null) => void;
    scheduleType?: string;
}

const ScheduleTaskView = ({
    showAddButton,
    readonly,
    currentTime,
    selectedSchedule,
    allTasks,
    selectedTask,
    selectedTelecommand,
    recentlyEditedTelecommand,
    onMenuClick,
    onTaskSelect,
    onCommandSelect,
    onAddTask,
    onAddCommand,
    scheduleType
}: ScheduleTaskViewProps) => {
    const [expanded, setExpanded] = useState(true)
    const [tab, setTab] = useState<TabType>('telecommand')
    const [openMenu, setOpenMenu] = useState<string | boolean>(false)
    const recentlyEditedRowRef = useRef<HTMLDivElement | null>(null)

    const selectedTaskCommands = useMemo(() => selectedTask?.commandsList || [], [selectedTask])
    const disabledCommandSection = useMemo(() => !selectedTask, [selectedTask])
    const hasCommands = useMemo(() => selectedTaskCommands?.length > 0, [selectedTaskCommands])
    const filterTasks = useMemo(() => Array.from(new Map(allTasks.map(item => [item.taskId, item])).values()), [allTasks])

    const ongoingSchedule = useMemo(() => selectedSchedule?.status === 'RUNNING', [selectedSchedule])
    const executedSchedule = useMemo(() => selectedSchedule?.status === 'COMPLETED', [selectedSchedule])
    const hideGroupButtons = useMemo(() => ongoingSchedule || executedSchedule, [ongoingSchedule, executedSchedule])
    const scheduleId = useMemo(() => selectedSchedule?.scheduleId, [selectedSchedule])
    const recentlyEditedCommandId = recentlyEditedTelecommand?.commandId
    const recentlyEditedCommandKey = recentlyEditedTelecommand?.updatedAt
    const showRecentlyEditedCommand = !!recentlyEditedCommandId && (!recentlyEditedTelecommand?.taskId || recentlyEditedTelecommand.taskId === selectedTask?.taskId)

    useEffect(() => {
        if (!showRecentlyEditedCommand) return
        setTab('telecommand')
        setExpanded(true)
    }, [recentlyEditedCommandKey, showRecentlyEditedCommand])

    useEffect(() => {
        if (!showRecentlyEditedCommand || tab !== 'telecommand' || !expanded) return
        return scrollAndBlinkEditedRow(recentlyEditedRowRef.current)
    }, [expanded, recentlyEditedCommandKey, showRecentlyEditedCommand, tab])

    const getDetails = (start: number, end: number) => {
        const baseDetails = {
            status: 'Upcoming',
            icon: <TaskUpcommingSvg />
        }
        if (!selectedSchedule || !selectedSchedule?.status || !currentTime) return baseDetails
        const scheduleStatus = selectedSchedule?.status as ScheduleStatusType
        const showDynamicDetails = ['RUNNING', 'COMPLETED'].includes(scheduleStatus)
        const expired = ['EXPIRED', 'ABOUT_TO_EXPIRE'].includes(scheduleStatus)
        if (['EXPIRED', 'ABORTED', 'ABOUT_TO_EXPIRE'].includes(scheduleStatus)) {
            return {
                status: expired ? 'Expired' : 'Aborted',
                icon: <TaskNotPerformedSvg />
            }
        }
        else if (showDynamicDetails && currentTime && readonly) {
            const status = getContactCurrentStatus(start, end, currentTime)
            const icon = get_task_status_icon(start, end, currentTime)
            return {
                status,
                icon
            }
        }
        return baseDetails
    }

    return (
        <div className='right__section__body'>
            <div className='schedule__task__container'>
                <div className='schedule__table__container'>
                    <div className='schedule__table__container__header'>
                        <div className='table__header__cell__container'>
                            <span className='table__cell header__cell w-4' />
                            <div className='table__cell header__cell w-30'>
                                <span className='cell__detail'>TASK</span>
                            </div>
                            <div className='table__cell header__cell w-30'>
                                <span className='cell__detail'>TASK DETAILS</span>
                            </div>
                            <div className='table__cell header__cell w-25'>
                                <span className='cell__detail'>START TIME</span>
                            </div>
                            <div className='table__cell header__cell w-10'>
                                <span className='cell__detail'>DURATION</span>
                            </div>
                        </div>
                    </div>
                    {(!filterTasks || filterTasks.length === 0) &&
                        <EmptyPage
                            classNames={readonly ? '' : 'cursor-pointer'}
                            icon={<NoSchedulePreview />}
                            content={scheduleType === 'Build' ? 'No Tasks Added Yet' : 'No Schedule to Preview'}
                            subContent={scheduleType === 'Build' ? 'Add a task to start building your schedule' : 'Please generate a schedule to preview'}
                        />
                    }
                    {(filterTasks && filterTasks.length > 0) && <div className='schedule__table__container__body'>
                        {filterTasks.map((item) => {
                            const selected = item.taskId === selectedTask?.taskId
                            const status = item?.status
                            const disabled = status === "Not Possible"
                            const message = item?.message || status || ''
                            const view = readonly ? 'readonly' : 'editable'
                            const showButton = showAddButton === 'add-task' && selected
                            const details = getDetails(item?.startTime, item?.endTime)
                            const taskTypeLabel = getTaskTypeDisplayLabel(item?.type)
                            return (
                                <div key={item.taskId}
                                    className={`table__body__cell__container
                                        ${selected ? 'active' : ''}
                                        ${disabled ? 'disabled' : ''}
                                        ${showButton ? 'show__button' : ''}
                                        `}
                                    onClick={() => {
                                        if (disabled) return
                                        onTaskSelect(view, item)
                                    }}
                                >
                                    {(!readonly && !disabled) &&
                                        <NewAddButton
                                            title='Add New Task'
                                            onClick={() => onAddTask?.(item)}
                                        />
                                    }
                                    <div className='table__cell w-4'>
                                        <div className='cell__icon'>
                                            {(ongoingSchedule || executedSchedule || disabled) && <CustomToolTip title={disabled ? message : details?.status} placement='top'>
                                                {disabled ? <TaskNotPerformedSvg /> : details?.icon}
                                            </CustomToolTip>}
                                        </div>
                                    </div>
                                    <div className='table__cell w-30'>
                                        <div className='cell__detail'>
                                            <CustomToolTip title={item?.taskName} placement='top'>
                                                {item?.taskName}
                                            </CustomToolTip>
                                        </div>
                                    </div>
                                    <div className='table__cell w-30'>
                                        <div className='cell__detail'>
                                            <CustomToolTip title={taskTypeLabel} placement='top'>
                                                {taskTypeLabel}
                                            </CustomToolTip>
                                        </div>
                                    </div>
                                    <div className='table__cell w-25'>
                                        <div className='cell__detail'>
                                            <CustomToolTip title={disabled ? '' : item?.startTimeISO8601} placement='top'>
                                                {disabled ? '--' : item?.startTimeISO8601}
                                            </CustomToolTip>
                                        </div>
                                    </div>
                                    <div className='table__cell w-10'>
                                        <div className='cell__detail'>
                                            <CustomToolTip title={disabled ? '--' : `${item?.duration ?? 0} sec`} placement='top'>
                                                {disabled ? '--' : `${item?.duration ?? 0} sec`}
                                            </CustomToolTip>
                                        </div>
                                    </div>
                                    {(!hideGroupButtons && !disabled) &&
                                        <div className='menu__container'>
                                            <GroupButtonList
                                                placement='left'
                                                id={item?.taskId as string}
                                                open={openMenu}
                                                hiddenButtons={['Edit']}
                                                toggle={(value) => {
                                                    if (disabled) return
                                                    setOpenMenu(value)
                                                }}
                                                buttons={MenuList}
                                                onClick={(btn) => {
                                                    onMenuClick(btn, view, 'task', {
                                                        name: item?.taskName,
                                                        scheduleId: scheduleId as string,
                                                        taskId: item?.taskId as string,
                                                    })
                                                }}
                                            >
                                                <i className='fe fe-more-vertical opacity-75' />
                                            </GroupButtonList>
                                        </div>
                                    }
                                </div>
                            )
                        })}
                    </div>
                    }
                </div>
            </div>
            <div className={`schedule__telecommand__container 
                ${disabledCommandSection ? 'disabled' : ''}
                ${(expanded && !disabledCommandSection) ? 'active' : ''}
                `}>
                <div className='schedule__telecommand__container__header'>
                    <div className='schedule__telecommand__container__header__button__container'>
                        {TabOptions.map((item) => {
                            return (
                                <button key={item.value} className={`telecommand__container__header__button ${tab === item.value ? 'active' : ''}`}
                                    onClick={() => {
                                        setTab(item.value as TabType)
                                        setExpanded(true)
                                    }}
                                >
                                    {item.label}
                                </button>
                            )
                        })}
                    </div>
                    <CustomToolTip title={expanded ? 'Close' : 'Open'} placement='top'>
                        <button className='schedule__telecommand__container__button'
                            onClick={() => setExpanded(!expanded)}
                        >
                            <ChevronDownSvg />
                        </button>
                    </CustomToolTip>
                </div>
                {!hasCommands &&
                    <EmptyPage
                        classNames={readonly ? '' : 'cursor-pointer'}
                        icon={readonly ? <NoCommandSelectedIcon /> : <AddNewCommandIcon />}
                        content={readonly ? 'No Telecommands' : 'No Telecommands to Preview'}
                        subContent={readonly ? '' : 'Click anywhere to start adding telecommand'}
                        onClick={() => { !readonly && onAddCommand?.(selectedTask!, null) }}
                    />
                }
                {(hasCommands && tab === 'telecommand') &&
                    <div className='schedule__table__container telecommand__table'>
                        <div className='schedule__table__container__header'>
                            <div className='table__header__cell__container'>
                                <span className='table__cell header__cell w-4' />
                                <span className='table__cell header__cell w-4' />
                                <div className='table__cell header__cell w-30'>
                                    <span className='cell__detail'>TELECOMMAND</span>
                                </div>
                                <div className='table__cell header__cell w-25'>
                                    <span className='cell__detail'>SUBSYSTEM</span>
                                </div>
                                <div className='table__cell header__cel w-25'>
                                    <span className='cell__detail'>START TIME</span>
                                </div>
                                <div className='table__cell header__cel w-10'>
                                    <span className='cell__detail'>DURATION</span>
                                </div>
                            </div>
                        </div>

                        <div className='schedule__table__container__body'>
                            {selectedTaskCommands.map((item) => {
                                const selected = item?.commandId === selectedTelecommand?.commandId;
                                const recentlyEdited = showRecentlyEditedCommand && item?.commandId === recentlyEditedCommandId
                                const view = readonly ? 'readonly' : 'editable'
                                const showButton = showAddButton === 'add-telecommand' && selected
                                const details = getDetails(item?.epoch, item?.epoch + item?.duration)
                                return (
                                    <div key={item?.commandId} className={`table__body__cell__container 
                                        ${selected ? 'active' : ''}
                                        ${recentlyEdited ? 'recently__edited__row' : ''}
                                        ${showButton ? 'show__button' : ''}
                                        `}
                                        ref={recentlyEdited ? recentlyEditedRowRef : undefined}
                                        onClick={() => { onCommandSelect(view, item) }}
                                    >
                                        {(!readonly) &&
                                            <NewAddButton
                                                title='Add New Telecommand'
                                                onClick={() => { onAddCommand?.(selectedTask!, item) }}
                                            />
                                        }
                                        <div className='table__cell w-4' >
                                            <div className='cell__icon'>
                                                {(ongoingSchedule || executedSchedule) && <CustomToolTip title={details?.status || ''} placement='top'>
                                                    {details?.icon}
                                                </CustomToolTip>}
                                            </div>
                                        </div>
                                        <div className='table__cell w-4' >
                                            <div className='cell__icon'>
                                                <CustomToolTip title={item?.type} placement='top'>
                                                    <TeleCommandsIcon />
                                                </CustomToolTip>
                                            </div>
                                        </div>
                                        <div className='table__cell w-30'>
                                            <div className='cell__detail'>
                                                <CustomToolTip title={item?.name} placement='top'>
                                                    {item?.name}
                                                </CustomToolTip>
                                            </div>
                                        </div>
                                        <div className='table__cell w-25'>
                                            <div className='cell__detail'>
                                                <CustomToolTip title={item?.subsystem} placement='top'>
                                                    {item?.subsystem}
                                                </CustomToolTip>
                                            </div>
                                        </div>
                                        <div className='table__cell w-25'>
                                            <div className='cell__detail'>
                                                <CustomToolTip title={item?.epochISO8601} placement='top'>
                                                    {item?.epochISO8601}
                                                </CustomToolTip>
                                            </div>
                                        </div>
                                        <div className='table__cell w-10'>
                                            <div className='cell__detail'>
                                                <CustomToolTip title={`${item?.duration} sec`} placement='top'>
                                                    {item?.duration ?? 0} sec
                                                </CustomToolTip>
                                            </div>
                                        </div>
                                        {!hideGroupButtons &&
                                            <div className='table__cell menu__container'>
                                                <GroupButtonList
                                                    placement='left'
                                                    id={item?.commandId as string}
                                                    open={openMenu}
                                                    toggle={(value) => {
                                                        setOpenMenu(value)
                                                    }}
                                                    buttons={MenuList}
                                                    onClick={(btn) => {
                                                        onMenuClick(btn, view, 'telecommand', {
                                                            name: item?.name,
                                                            scheduleId: scheduleId as string,
                                                            taskId: selectedTask?.taskId as string,
                                                            commandId: item?.commandId as string,
                                                        })
                                                    }}
                                                >
                                                    <i className='fe fe-more-vertical opacity-75' />
                                                </GroupButtonList>
                                            </div>
                                        }
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                }

                {(hasCommands && tab === 'raw-data') &&
                    <div className='schedule__table__container pb-2'>
                        <div className='schedule__table__container__body gap-3'>
                            {selectedTaskCommands.map((item) => {
                                return (
                                    <div key={item?.commandId} className='row__data__container'>
                                        <div className='row__details__icon__container'>
                                            <TaskUpcommingSvg />
                                        </div>
                                        <div className='row__data__details__container'>
                                            <span className='row__data__details__text'>
                                                TC/TM COMMAND | {item?.subsystem}
                                            </span>
                                            <span className='row__data__details__text'>
                                                {base64ToHex(item?.sequenceDetails?.sequenceBase64)}
                                            </span>
                                        </div>
                                        <div className='row__data__badge'>
                                            <span className='row__detail__time'>{item?.epochISO8601}</span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                }
            </div>
        </div >
    )
}

export default ScheduleTaskView
